// Functions for AI Chat - includes CRUD Operations for localStorage, but specifically for AI Chat

import { AI_CHAT_LOCAL_STORAGE_KEY, DEVICE_ID_KEY } from "./constants";
import { decodeUserJWT, retrieveDeviceId } from "./utils";

// ====================================================
// ----- DATA DEFINITIONS -----

// Define list of pages for messages to be fetched and when chat notifications can be shown
export const pagesToFetchMessages = [
  "about",
  "auth",
  "buy_packs",
  "contact_us",
  "dashboard",
  "favorites",
  "guide",
  "history",
  "help/time",
  "home",
  "item",
  "login",
  "openAuctions",
  "payment",
  "profile",
  "reset_password",
  "rewards",
  "search",
  "setup_account",
  "sign_up",
  "shipping",
  "terms-and-conditions",
  "winners",
  "winnings"
];

//messages for each page
// NB: key here (home, login, etc) must match page name exactly! (controlled by 'aiPageControl')
// content is in the translations files for i18n - match the key name (eg home-1)
// to add, follow the style below then match the key in translation files with desired question
// to add more than one per page, add to the array (e.g. home: ["home-1", "home-2"])
export const pageMessagesDefined = {
  about: ["about-1"],
  buy_packs: ["buy-packs-1"],
  contact_us: ["contact-us-1"],
  guide: ["guide-1"],
  favorites: ["favorites-1"],
  history: ["history-1"],
  home: ["home-1"],
  item: ["item-1"],
  login: ["login-1"],
  openAuctions: ["open-auctions-1"],
  payment: ["payment-1"],
  profile: ["profile-1"],
  rewards: ["rewards-1"],
  sign_up: ["sign_up-1"],
  shipping: ["shipping-1"],
  winners: ["winners-1"],
  winnings: ["winnings-1"]
};

// ====================================================

// ---- PAGE CONTROL / CHECK FUNCTIONS ---------
// check if page is part of list for questions
// to make changes:
// 1) change the 'pagesToFetchMessages' array above
// 2) Some pages (login, sign-up, terms-and-conditions are not pages, but rather modals - consider 'aiControl' below
//    that determines and sets page names)
// Consider current users local storage tracking before making changes
export const checkValidPage = (page: string): boolean => {
  return pagesToFetchMessages.includes(page);
};

// Set initial user data in local storage
export function setInitialAIUserChatLocalStorage(): void {
  // set default data
  // timestamp is set to 0 ensuring data is updated if user logs in
  const data = {
    user_data: {
      uuid: null,
      timestamp: 0,
      nickname: "",
      bidsPlaced: 0,
      referrals: 0,
      bidsBought: false
    }
  };

  localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(data));
}

// Use only for logged in users
// check for uuid in localStorage and set it if not present
// return true - either if uuid present or if set
export function checkForUuid(): {
  success: boolean;
  errorString: string | null;
} {
  const lsData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);
  if (!lsData)
    return {
      success: false,
      errorString: "No user data in local storage for uuid check"
    };
  try {
    const data = JSON.parse(lsData);
    if (data.user_data && data.user_data.uuid) {
      return { success: true, errorString: null };
    } else {
      const token = localStorage.getItem("refreshToken");
      if (!token)
        return {
          success: false,
          errorString: "No refresh token in local storage for uuid check"
        };
      const decodedToken = decodeUserJWT(token);
      if (decodedToken && decodedToken.uuid) {
        data.user_data.uuid = decodedToken.uuid;
        localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(data));
        return { success: true, errorString: null };
      } else {
        return {
          success: false,
          errorString: "Could not decode refresh token for uuid check"
        };
      }
    }
  } catch (error) {
    return {
      success: false,
      errorString: "Could not parse user data from local storage for uuid check"
    };
  }
}

// set / overwrite AI Chat user data in localStorage
export const setAIChatUserDataLocalStorage = (data: AiUserData): boolean => {
  // Validate
  if (!data.uuid || typeof data.uuid !== "string") {
    console.error("Invalid AI User Data: 'uuid' is missing or not a string");
    return false;
  }
  if (
    data.timestamp === undefined ||
    data.timestamp === null ||
    typeof data.timestamp !== "number"
  ) {
    console.error(
      "Invalid AI User Data: 'timestamp' is missing or not a number"
    );
    return false;
  }
  if (!data.nickname || typeof data.nickname !== "string") {
    console.error(
      "Invalid AI User Data: 'nickname' is missing or not a string"
    );
    return false;
  }
  if (
    data.bidsPlaced === undefined ||
    data.bidsPlaced === null ||
    typeof data.bidsPlaced !== "number"
  ) {
    console.error(
      "Invalid AI User Data: 'bidsPlaced' is missing or not a number"
    );
    return false;
  }
  if (
    data.referrals === undefined ||
    data.referrals === null ||
    typeof data.referrals !== "number"
  ) {
    console.error(
      "Invalid AI User Data: 'referrals' is missing or not a number"
    );
    return false;
  }
  if (typeof data.bidsBought !== "boolean" || data.bidsBought === null) {
    console.error(
      "Invalid AI User Data: 'bidsBought' is missing or not a boolean"
    );
    return false;
  }

  const localData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);

  if (localData) {
    // if data is present, set the user_data
    let jsonData = JSON.parse(localData);
    jsonData.user_data = data;
    localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(jsonData));
  } else {
    // no data in local storage
    const newData = {
      user_data: data
    };
    localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(newData));
  }
  return true;
};

export function getAIChatUserData(): AiUserData | null {
  const localData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);
  if (!localData) return null;

  try {
    const jsonData = JSON.parse(localData);
    if (!jsonData.user_data) {
      return null;
    }

    // validate
    let message = "";

    if (typeof jsonData.user_data.timestamp !== "number") {
      message += "timestamp is not a number. ";
    }

    if (typeof jsonData.user_data.nickname !== "string") {
      message += "nickname is not a string. ";
    }

    if (typeof jsonData.user_data.bidsPlaced !== "number") {
      message += "bidsPlaced is not a string. ";
    }

    if (typeof jsonData.user_data.referrals !== "number") {
      message += "referrals is not a string. ";
    }

    if (typeof jsonData.user_data.bidsBought !== "boolean") {
      message += "bidsBought is not a string. ";
    }

    if (message !== "") {
      console.error("Invalid AI User Data format in localStorage: ", message);
      return null;
    }

    // uuid can be null
    if (
      jsonData.user_data.uuid &&
      typeof jsonData.user_data.uuid !== "string"
    ) {
      console.error(
        "Invalid AI User Data format in localStorage - uuid is not a string"
      );
      return null;
    }

    return jsonData.user_data as AiUserData;
  } catch (error) {
    console.error("Error parsing Chat JSON data from localStorage:", error);
    return null;
  }
}

// get last updatedTime for User Data - AI Chat
export const getLastUserDataUpdateChat = (): number | null => {
  const lsData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);

  if (lsData === null) {
    return null;
  }
  try {
    const data = JSON.parse(lsData);
    if (
      data.user_data &&
      data.user_data.timestamp !== undefined &&
      data.user_data.timestamp !== null
    ) {
      return data.user_data.timestamp;
    } else {
      console.error("AI Chat Error - Missing timestamp Value");
      return null;
    }
  } catch (error) {
    console.error(
      "Error parsing Chat JSON data from localStorage for last update val:",
      error
    );
    return null;
  }
};

// Check for page data engagement in local storage
// will also return user_data if found and there are no errors
export const getPageDataLocalStorage = (
  pageName: string
): {
  pageData: string[];
  userData: AiUserData | null;
  error?: string;
  fullPagesData?: { [key: string]: string[] };
} => {
  const localData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);
  const deviceIDStorage = localStorage.getItem(DEVICE_ID_KEY);

  try {
    //check for device id - generate if needed (should not happen - deviceID is set in socket too)
    if (!deviceIDStorage) {
      retrieveDeviceId();
    }

    // no data for user - we send null for the deviceID (genrated or not)
    if (!localData) return { pageData: [], userData: null };

    let userData: AiUserData | null = null;

    const pageData = JSON.parse(localData).page_data;
    userData = JSON.parse(localData).user_data;
    // page data, but not for this page
    if (!pageData) {
      if (userData === null) {
        console.error(
          `AI Chat Error - unexpected value in local storage for key ${AI_CHAT_LOCAL_STORAGE_KEY}`
        );
      } else {
        return {
          pageData: [],
          userData: userData,
          fullPagesData: pageData
        };
      }
    }

    if (pageData[pageName]) {
      const pageDataArray = pageData[pageName];
      if (!Array.isArray(pageDataArray)) {
        console.error(`Invalid page data format for page: ${pageName}`);
        return {
          pageData: [],
          userData: null,
          error: "Invalid page data format"
        };
      }

      // Validate each item question (content)
      for (const item of pageDataArray) {
        if (typeof item !== "string") {
          console.error(
            `Invalid item format in page data for page: ${pageName}`
          );
          return {
            pageData: [],
            userData: null,
            error: "Invalid page data format",
            fullPagesData: pageData
          };
        }
      }
      return {
        pageData: pageDataArray,
        userData: userData ? userData : null,
        fullPagesData: pageData
      };
    } else {
      return {
        pageData: [],
        userData: userData ? userData : null,
        fullPagesData: pageData
      };
    }
  } catch (error) {
    console.error(
      `Error parsing Chat JSON data from localStorage for page data: ${error}`
    );
    return {
      pageData: [],
      userData: null,
      error: "Error parsing Chat JSON data from localStorage for page data"
    };
  }
};

// each question for a page is stored as an array of strings
// if the question was engaged with (added to chat or if close was clicked) - not necessarily asked - then it
// gets added to Local Storage (in local storage means completed and cannot be asked again) -- not done here
// does NOT check if this a valid page, must be done before function call
export const getCurrentPageQuestion = (pageName: string): string | null => {
  // first check if there are questions defined for this page
  const pageQuestionsArr =
    pageMessagesDefined[pageName as keyof typeof pageMessagesDefined];
  if (!pageQuestionsArr || pageQuestionsArr.length === 0) {
    // no defined questions for the page
    return null;
  }

  const pageDataLocalStorage = getPageDataLocalStorage(pageName);
  // if there was an error, don't return anything (ensure modal is not shown)
  if (pageDataLocalStorage.error) {
    return null;
  }

  // page data field or data for this page does not exist in local storage
  if (pageDataLocalStorage.pageData.length === 0) {
    // If no data for this page, add first question to the ai Notification for the current page (do not set in local storage unless engaged with - not done here)
    if (pageQuestionsArr && pageQuestionsArr.length > 0) {
      return pageQuestionsArr[0];
    } else {
      // no defined questions for this page
      return null;
    }
  } else {
    // there is existing page data for this page
    // get index of last asked question
    const lastQuestionIndex = pageDataLocalStorage.pageData.length - 1;
    const currentQuestionArray =
      pageMessagesDefined[pageName as keyof typeof pageMessagesDefined];

    // check for more questions
    if (
      currentQuestionArray[lastQuestionIndex] &&
      currentQuestionArray[lastQuestionIndex].length > 0 &&
      lastQuestionIndex + 1 < currentQuestionArray.length
    ) {
      return currentQuestionArray[lastQuestionIndex + 1];
    } else {
      // no more questions - return null
      return null;
    }
  }
};

// question_ref would be something like home-1
export const saveQuestionEngagementLocalStorage = (
  pageName: string,
  questionRef: string
): void => {
  if (
    pageName === undefined ||
    pageName === null ||
    typeof pageName !== "string" ||
    pageName === ""
  ) {
    console.error(
      "Invalid page name provided for saving question engagement:",
      pageName
    );
  }

  if (
    questionRef === undefined ||
    questionRef === null ||
    typeof questionRef !== "string" ||
    questionRef === ""
  ) {
    console.error(
      "Invalid question reference provided for saving question engagement:",
      pageName
    );
  }

  if (!pagesToFetchMessages.includes(pageName)) {
    console.error(
      "Invalid page name provided for saving question engagement:",
      pageName
    );
  }

  try {
    // Retrieve current data from local storage
    const pageDataLocalStorage = getPageDataLocalStorage(pageName);
    // Prepare updated page data
    const updatedPageData = pageDataLocalStorage.fullPagesData || {};
    if (!updatedPageData[pageName]) {
      updatedPageData[pageName] = [];
    }
    updatedPageData[pageName].push(questionRef);

    // Construct new data object
    const newData: {
      page_data: { [key: string]: string[] };
      user_data?: AiUserData;
    } = {
      page_data: updatedPageData,
      ...(pageDataLocalStorage.userData && {
        user_data: pageDataLocalStorage.userData
      })
    };

    // Write updated data to local storage
    localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(newData));
  } catch (error) {
    console.error("Error saving question engagement to local storage:", error);
  }
};
